import React from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Formik } from 'formik';

import {
  selectGiftCardsOrderDirectDelivery,
  selectGiftCardsOrderCards,
  selectGiftCardsOrderAddress,
} from '@selectors/giftCards';
import { selectIsUserLogged } from '@selectors/auth';
import { selectCustomerDetailsPersonal } from '@selectors/customerDetail';
import { setGiftCardsOrderAddress } from '@actions/giftCards';

import { getRouteUrl } from '@utils/urls';

import { GIFT_CARDS_PURCHASE_SUMMARY } from '@common/routes';
import { giftCardsPageDataShape } from '@common/types/giftCards';
import Accordion from '@common/components/Accordion';
import CMSComponentsRenderer from '@common/components/CMSComponents/CMSComponentsRenderer';
import PaymentBenefitBlock from '@common/components/PaymentBenefitBlock';
import PageHeroMedia from '@common/components/PageHeroMedia';

import {
  useInitialValues,
  validationSchema,
} from './GiftCardsPurchase.helpers';
import DiscountMessage from './DiscountMessage';
import DeliveryOptions from './DeliveryOptions';
import AddCard from './AddCard';
import Order from './Order';
import TotalValue from './TotalValue';
import LoginMessage from './LoginMessage';
import Addresses from './Addresses';
import SubmitButton from './SubmitButton';
import {
  Title,
  Section,
  SectionTitle,
  GiftCardHeroWrapper,
  GiftCardsPurchaseWrapper,
  GiftCardsPurchaseContent,
} from './GiftCardsPurchase.styled';

const GiftCardsPurchase = ({ pageData: { title, body, image } }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const dispatch = useDispatch();
  const directDelivery = useSelector(selectGiftCardsOrderDirectDelivery);
  const giftCards = useSelector(selectGiftCardsOrderCards);
  const orderAddress = useSelector(selectGiftCardsOrderAddress);
  const isUserLogged = useSelector(selectIsUserLogged);
  const customerDetail = useSelector(selectCustomerDetailsPersonal);

  const isDeliverySelected = directDelivery !== null;
  const hasGiftCards = !!Object.keys(giftCards).length;
  const canSubmit = isDeliverySelected && hasGiftCards;

  const initialValues = useInitialValues(
    orderAddress,
    directDelivery,
    isUserLogged,
    customerDetail,
  );

  const handleSubmit = async ({ address }) => {
    await dispatch(setGiftCardsOrderAddress(address));
    router.push(
      {
        pathname: GIFT_CARDS_PURCHASE_SUMMARY,
      },
      {
        pathname: getRouteUrl(router.locale, GIFT_CARDS_PURCHASE_SUMMARY),
      },
    );
  };

  return (
    <React.Fragment>
      <GiftCardHeroWrapper>
        <PageHeroMedia image={{ src: image?.url, alt: image?.altText || '' }} />
      </GiftCardHeroWrapper>

      <GiftCardsPurchaseWrapper>
        <GiftCardsPurchaseContent>
          <Formik
            initialValues={initialValues}
            enableReinitialize
            validateOnMount
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            <Form>
              <Title>{title}</Title>

              {body && (
                <Accordion
                  title={t('giftCards.order.info.title', 'Infos zur Karte')}
                  isFirstItem
                >
                  <CMSComponentsRenderer body={body} />
                </Accordion>
              )}

              <DiscountMessage />

              <Section>
                <SectionTitle>
                  {t('giftCards.order.delivery.title', 'Lieferung')}
                </SectionTitle>

                <DeliveryOptions />
              </Section>

              {isDeliverySelected && (
                <Section>
                  <SectionTitle>
                    {t('giftCards.order.cards.title', 'Bestellung')}
                  </SectionTitle>

                  <AddCard />

                  <Order />

                  <TotalValue />
                </Section>
              )}

              {hasGiftCards && (
                <Section>
                  <LoginMessage />

                  <SectionTitle>
                    {t('giftCards.order.adressOrder.title', 'Adressangaben')}
                  </SectionTitle>

                  <Addresses
                    invoiceAddressTitle={
                      <SectionTitle>
                        {t(
                          'giftCards.order.adressBilling.title',
                          'Rechungsadresse',
                        )}
                      </SectionTitle>
                    }
                  />
                </Section>
              )}

              <SubmitButton canSubmit={canSubmit} />

              <PaymentBenefitBlock
                spacingTop={['section', 1]}
                showOnlyPaymentMethods
              />
            </Form>
          </Formik>
        </GiftCardsPurchaseContent>
      </GiftCardsPurchaseWrapper>
    </React.Fragment>
  );
};

GiftCardsPurchase.propTypes = {
  pageData: giftCardsPageDataShape.isRequired,
};

export default GiftCardsPurchase;
